import React, { useLayoutEffect, useRef, useState } from "react";
import {
  BottomSheet,
  Button,
  Modal,
  Tabs,
  useIsMobile,
} from "../../../components/ui";
import BrandDocument, {
  FRAME_CHROME,
  PAGE_WIDTH,
  PREVIEW_DOCUMENTS,
} from "./BrandDocument";
import { BrandDetails, BrandPreviewId } from "../types/brand.types";

interface BrandPreviewSheetProps {
  details: BrandDetails;
  /** Which document is being read. Shared with the column — see `MyBrandPage`. */
  active: BrandPreviewId;
  onChange: (id: BrandPreviewId) => void;
  open: boolean;
  onClose: () => void;
}

/**
 * The documents the brand produces, for the screens too narrow to stand one
 * beside the form.
 *
 * One at a time behind a tab strip, the way the column beside the form shows
 * one behind its switch: the pages are tall, and stacking both would put the
 * invoice a screen and a half below the meal plan — reached by scrolling past
 * a document rather than by asking for it.
 *
 * Which one is open is the page's state rather than this component's, so the
 * panel opens on whatever was last looked at and a screen widened past `lg`
 * finds the column showing the same document.
 *
 * On a phone it comes up from the bottom edge rather than sitting in the
 * middle of the screen, the way the Form tab's preview does: both are the
 * same errand on the same screen — a look at what a client will be handed,
 * asked for by a Preview button in the header and read for as long as it
 * takes — and a phone that opens one of them as a drawer and the other as a
 * dialog is two answers to one question. The tablet keeps the dialog, having
 * the width to centre one.
 */
const BrandPreviewSheet: React.FC<BrandPreviewSheetProps> = ({
  details,
  active,
  onChange,
  open,
  onClose,
}) => {
  const boxRef = useRef<HTMLDivElement>(null);

  // Nothing is drawn until the box has been measured — a page laid out at some
  // guessed width and corrected a frame later reads as the sheet flinching as
  // it opens. Zero is what says "not measured yet"; a real scale is never it.
  const [scale, setScale] = useState(0);

  // Layout effect, not effect: the measurement and the page it decides both
  // have to land in the same paint as the dialog itself.
  // Which shell the documents are put in — see the note above.
  const isMobile = useIsMobile();

  useLayoutEffect(() => {
    // The panel is unmounted while closed, so there is nothing to measure and
    // the next opening measures afresh — which is what makes a phone turned on
    // its side between two openings draw at the width it now has.
    if (!open) {
      setScale(0);
      return;
    }

    const measure = () => {
      const box = boxRef.current;
      if (!box) return;

      // Fractional, unlike `clientWidth`: rounding the box up by half a pixel
      // is enough to push the page's right edge under the dialog's padding.
      const { width } = box.getBoundingClientRect();

      // The page has what is left once the frame has taken its own chrome -
      // the scrollbar the meal plan's stack turns beside, and the border
      // either side. Left out of the sum, the sheet is wider than the room
      // inside the border and its right edge is clipped.
      const next = (width - FRAME_CHROME) / PAGE_WIDTH;
      if (next > 0) setScale(next);
    };

    measure();

    // The sheet's width is the dialog's, which follows the window — so turning
    // the device re-scales the page rather than leaving it at the width the
    // other orientation had.
    window.addEventListener("resize", measure);

    // A resize is not the only thing that changes the box: the web font
    // swapping in after first paint re-flows the dialog around it.
    const observer =
      typeof ResizeObserver === "undefined" ? null : new ResizeObserver(measure);
    const box = boxRef.current;
    if (box) observer?.observe(box);

    return () => {
      window.removeEventListener("resize", measure);
      observer?.disconnect();
    };
    // The two shells draw their own box, so a window crossing `md` with the
    // panel open measures the one it now has rather than keeping the width of
    // the one that has gone.
  }, [open, isMobile]);

  /** The line, the strip and the page — the same three in whichever shell is
   *  drawn. Only the padding around them differs, and the sheet brings its
   *  own. */
  const content = (
    <>
      <p className="text-[13px] 3xl:text-[14px] leading-5 text-ink-muted">
        How your details appear on the documents your clients receive. Specimen
        values stand in for anything not filled in yet.
      </p>

      {/* The app's own tab strip rather than the column's segmented control:
          this is a full-width row at the head of a panel, which is the shape
          every other tabbed screen uses, and the control was drawn to sit
          quietly in the corner above a document. */}
      <div className="mt-4 border-b border-line">
        <Tabs
          tabs={PREVIEW_DOCUMENTS}
          activeTab={active}
          onChange={onChange}
        />
      </div>

      {/* The measuring box is the flow column, so the page below is drawn to a
          width that is already settled — see `scale`. It is measured whichever
          document is open, and both are the same page, so switching tabs never
          re-scales anything. */}
      <div ref={boxRef} className="mt-4">
        {scale > 0 && (
          <BrandDocument id={active} details={details} scale={scale} />
        )}
      </div>
    </>
  );

  if (isMobile) {
    return (
      // No footer, as the Form tab's preview has none: there is nothing to
      // answer here — the sheet is a look at a document — and a pinned row
      // would take the width off the page it was opened to show. It is left
      // by pressing the screen above it, which is how every read-only sheet
      // in the app is left.
      <BottomSheet open={open} title="Preview" onClose={onClose}>
        <div className="px-5 pb-2 pt-1">{content}</div>
      </BottomSheet>
    );
  }

  return (
    <Modal
      open={open}
      title="Preview"
      onClose={onClose}
      footer={
        <Button size="md" variant="secondary" onClick={onClose}>
          Close
        </Button>
      }
    >
      {content}
    </Modal>
  );
};

export default BrandPreviewSheet;
